import { ErrorCodes, IMPERSONATION_REQUEST_HEADER } from '@constants';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { catchError, filter, from, map, merge, Observable, of, tap } from 'rxjs';

import { useCacheStore } from '~/store/cache';
import { useImpersonationStore } from '~/store/impersonation';
import { request } from '~/support/utils';
import { endSession, isRefreshablePath, refreshAccessToken, ValidationError } from '~/support/utils';
import type { RestApiResponse } from '~/types';

import { catchUnauthorized } from '../operators';
import { UnauthorizedException } from '../utils/errors';
import { useAuthStore } from '~/store/auth';
import { useMerchantStore } from '~/store/merchant';

type ObservableRESTResponse<T> = Observable<RestApiResponse<T>>;

type RetryableRequestConfig = AxiosRequestConfig & { retriedWithFreshToken?: boolean };

// Skips the cache-first emission and cache write, for calls where a stale paint would be wrong.
type GetOptions = { noCache?: boolean };

export class HttpObservableClient {
  cache: ReturnType<typeof useCacheStore>;
  constructor(private http: AxiosInstance) {
    this.cache = useCacheStore();
  }

  private intercept(request: Promise<AxiosResponse>): Observable<AxiosResponse> {
    return from(request).pipe(
      map((res: AxiosResponse) => res.data),
      catchUnauthorized(error => {
        if (error.getCode?.()) {
          throw error;
        }

        return of({ data: null, error: 'Unauthorized' });
      }),
    ) as any;
  }

  get<T>(url: string, config?: AxiosRequestConfig, { noCache }: GetOptions = {}): ObservableRESTResponse<T> {
    if (noCache) {
      return this.intercept(this.http.get(url, config));
    }

    const conf = {
      ...config,
      url,
    };

    const cached = this.cache.get(conf);

    return merge(
      of(cached).pipe(filter(res => !!res)) as ObservableRESTResponse<T>,
      this.intercept(this.http.get(url, config)).pipe(
        tap((res: AxiosResponse) => {
          this.cache.set(conf, res);
        }),
        catchError(error => {
          this.cache.remove(conf);

          throw error;
        }),
      ),
    );
  }

  post<T>(...args: Parameters<AxiosInstance['post']>): ObservableRESTResponse<T> {
    return this.intercept(this.http.post(...args));
  }

  put<T>(...args: Parameters<AxiosInstance['put']>): ObservableRESTResponse<T> {
    return this.intercept(this.http.put(...args));
  }

  delete<T>(...args: Parameters<AxiosInstance['delete']>): ObservableRESTResponse<T> {
    return this.intercept(this.http.delete(...args));
  }

  patch<T>(...args: Parameters<AxiosInstance['patch']>): ObservableRESTResponse<T> {
    return this.intercept(this.http.patch(...args));
  }

  request<T>(...args: Parameters<AxiosInstance['request']>): ObservableRESTResponse<T> {
    return from(this.http.request(...args));
  }
}

export default class GatewayBase {
  private instance: AxiosInstance;
  private http: HttpObservableClient;

  constructor(public baseUrl: string) {
    this.instance = request.create({
      baseURL: this.baseUrl,
    });

    this.setupInterceptors();
    this.http = new HttpObservableClient(this.instance);
  }

  private setupInterceptors() {
    const store = useAuthStore();
    const merchantStore = useMerchantStore();

    this.instance.interceptors.request.use(config => {
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${store.accessToken}`;
      }
      config.headers['X-Store-Id'] = merchantStore.storeIdsArray;

      // Send the impersonation cookie only while a session is active. Doing this per-request (rather than on the
      // axios instance) keeps every normal request uncredentialed, so a missing CORS allowlist can only break
      // impersonation rather than the entire app. The bootstrap control calls set withCredentials themselves.
      if (useImpersonationStore().shouldSendCredentials) {
        config.withCredentials = true;
      }

      // Keyed off the merged config rather than the store, so the control calls that set `withCredentials`
      // themselves are covered too. The gateway grants credentialed CORS only to requests carrying this header,
      // and the preflight advertises its name — which is the only impersonation signal visible on an OPTIONS.
      if (config.withCredentials) {
        config.headers[IMPERSONATION_REQUEST_HEADER] = '1';
      }

      return config;
    });

    this.instance.interceptors.response.use(null, async error => {
      if (!error.response) {
        throw error;
      }

      if (error.response.status === 401 && this.shouldRetryWithFreshToken(error.config)) {
        return await this.retryWithFreshToken(error);
      }

      switch (error.response.status) {
        case 400:
          if (error.response.data.errors) {
            throw new ValidationError(error.response.data.errors);
          } else {
            throw error;
          }
        case 401: {
          // A server-authoritative impersonation rejection ends the session immediately. Without this, detecting an
          // expired/terminated session relies solely on the banner's local countdown — and if that drifts (clock
          // skew, throttled tab) every ordinary call 401s and burns an engineer-token refresh-and-retry instead.
          const code = error.response.data?.code;
          if (typeof code === 'string' && code.startsWith('IMPERSONATION_')) {
            useImpersonationStore().stop();
          }
          throw new UnauthorizedException(code);
        }
        default:
          throw error;
      }
    });
  }

  private shouldRetryWithFreshToken(config?: RetryableRequestConfig): boolean {
    if (!config || config.retriedWithFreshToken) return false;

    return isRefreshablePath(config.url);
  }

  // An expired access token comes back as a bare 401 with no `code` in the body, which the
  // observable layer turns into `{ data: null }` — a silent empty page rather than a logout. So the
  // recovery has to happen here, where the original request is still replayable.
  private async retryWithFreshToken(error: { config: RetryableRequestConfig; response: AxiosResponse }) {
    error.config.retriedWithFreshToken = true;

    const outcome = await refreshAccessToken();

    if (outcome === 'refreshed') {
      // Overwrite rather than delete: this config still carries the expired token from the first
      // attempt, and the request interceptor only fills Authorization in when it's absent.
      error.config.headers = { ...error.config.headers, Authorization: `Bearer ${useAuthStore().accessToken}` };

      return await this.instance.request(error.config);
    }

    if (outcome === 'failed') {
      await endSession();

      throw new UnauthorizedException(ErrorCodes.Unauthorized.TOKEN_EXPIRED);
    }

    // 'no-session': an unauthenticated visitor (login, MFA, set-password pages). There is no
    // session to end and nowhere better to send them — surface the 401 exactly as if no retry had
    // been attempted; with no code attached, catchUnauthorized maps it to { data: null }.
    throw new UnauthorizedException(error.response.data?.code);
  }

  get client() {
    return this.http;
  }
}
